import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Music4, Plus, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import { listPlaylist, addPlaylistSong } from "@/lib/api";

const PAGE_SIZE = 5;
const ROTATE_MS = 10000;

export default function PlaylistCollaborative({ invitationId, enabled, preview = false }) {
  const [songs, setSongs] = useState([]);
  const [name, setName] = useState("");
  const [song, setSong] = useState("");
  const [artist, setArtist] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      try {
        const r = await listPlaylist(invitationId);
        setSongs(r);
      } catch { /* ignore */ }
    })();
  }, [invitationId, enabled]);

  // Rotate the visible window every ROTATE_MS while collapsed.
  const totalPages = Math.max(1, Math.ceil(songs.length / PAGE_SIZE));
  useEffect(() => {
    if (expanded || totalPages <= 1) return;
    const id = setInterval(() => setPage((p) => (p + 1) % totalPages), ROTATE_MS);
    return () => clearInterval(id);
  }, [expanded, totalPages]);

  useEffect(() => {
    if (page >= totalPages) setPage(0);
  }, [page, totalPages]);

  const visible = useMemo(() => {
    if (expanded) return songs;
    const start = page * PAGE_SIZE;
    return songs.slice(start, start + PAGE_SIZE);
  }, [songs, page, expanded]);

  if (!enabled) return null;

  const submit = async () => {
    if (preview) {
      toast.info("Vista previa: las canciones solo se guardan en la invitación real");
      return;
    }
    if (!name.trim() || !song.trim()) {
      toast.error("Tu nombre y la canción son requeridos");
      return;
    }
    try {
      setLoading(true);
      const r = await addPlaylistSong(invitationId, { guest_name: name.trim(), song: song.trim(), artist: artist.trim() });
      setSongs((p) => [r, ...p]);
      setPage(0);
      setName("");
      setSong("");
      setArtist("");
      toast.success("¡Canción sumada al playlist!");
    } catch {
      toast.error("No se pudo enviar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-14 px-6 bg-ink text-cream">
      <div className="text-center mb-8">
        <Music4 size={22} className="text-warmgold mx-auto" />
        <p className="overline !text-cream mt-3">Playlist colaborativa</p>
        <h3 className="headline text-4xl text-cream mt-2">Que suene tu canción</h3>
        <p className="text-cream/70 mt-3 max-w-sm mx-auto text-sm">Sugiere una canción para que el DJ la ponga en la fiesta.</p>
      </div>
      <div className="max-w-md mx-auto glass-dark rounded-3xl p-6">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-cream/70">Tu nombre</span>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="playlist-name" className="mt-2 w-full bg-transparent border-b border-cream/30 focus:border-warmgold focus:outline-none py-2 text-cream placeholder:text-cream/40" />
        </label>
        <label className="block mt-4">
          <span className="text-[10px] uppercase tracking-widest text-cream/70">Canción</span>
          <input value={song} onChange={(e) => setSong(e.target.value)} data-testid="playlist-song" className="mt-2 w-full bg-transparent border-b border-cream/30 focus:border-warmgold focus:outline-none py-2 text-cream" />
        </label>
        <label className="block mt-4">
          <span className="text-[10px] uppercase tracking-widest text-cream/70">Artista (opcional)</span>
          <input value={artist} onChange={(e) => setArtist(e.target.value)} data-testid="playlist-artist" className="mt-2 w-full bg-transparent border-b border-cream/30 focus:border-warmgold focus:outline-none py-2 text-cream" />
        </label>
        <button onClick={submit} disabled={loading} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="playlist-submit">
          <Plus size={14} /> Añadir al playlist
        </button>
      </div>
      {songs.length > 0 && (
        <div className="max-w-md mx-auto mt-8" data-testid="playlist-list">
          <AnimatePresence mode="wait">
            <motion.div
              key={expanded ? "all" : `page-${page}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5 }}
              className="space-y-2"
            >
              {visible.map((s) => (
                <div key={s.id} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
                  <Music4 size={14} className="text-warmgold shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-cream truncate">{s.song}{s.artist ? ` — ${s.artist}` : ""}</p>
                    <p className="text-cream/50 text-xs">Sugerida por {s.guest_name}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          {songs.length > PAGE_SIZE && (
            <div className="mt-5 flex flex-col items-center gap-3">
              {!expanded && totalPages > 1 && (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPage((p) => (p - 1 + totalPages) % totalPages)}
                    className="w-8 h-8 rounded-full border border-cream/25 grid place-items-center text-cream/70 hover:text-warmgold hover:border-warmgold transition-colors"
                    data-testid="playlist-prev"
                    aria-label="Anteriores"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <div className="flex gap-1.5" aria-hidden data-testid="playlist-dots">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <span key={i} className={`h-1.5 rounded-full transition-all ${i === page ? "w-6 bg-warmgold" : "w-1.5 bg-cream/30"}`} />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPage((p) => (p + 1) % totalPages)}
                    className="w-8 h-8 rounded-full border border-cream/25 grid place-items-center text-cream/70 hover:text-warmgold hover:border-warmgold transition-colors"
                    data-testid="playlist-next"
                    aria-label="Siguientes"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex items-center gap-2 text-cream/70 text-xs uppercase tracking-widest hover:text-warmgold transition-colors py-2"
                data-testid="playlist-toggle"
              >
                {expanded ? (
                  <>Ocultar canciones <ChevronUp size={14} /></>
                ) : (
                  <>Mostrar todas las canciones ({songs.length}) <ChevronDown size={14} /></>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
